/**
 * @file order-stage-transition.ts
 * @description Перевод заказа в стадию по её ВИДУ, а не по идентификатору
 * (phase19.24 шаг 5: кнопка «Выдать заказ» на самовывозе).
 *
 * У выдачи самовывоза своего эндпоинта нет — есть универсальный
 * `POST /admin/orders/:id/transition`, которому нужен `toStageId`. Карточка
 * знает только вид стадии (`DELIVERED`): имя и идентификатор стадии магазин
 * настраивает сам. Поэтому идентификатор ищем в воронке заказа и кэшируем на
 * время жизни вкладки — стадии меняют редко, а лишний запрос на каждый клик
 * тормозит выдачу у стойки.
 *
 * Стадии нужного вида в воронке нет — это настройка магазина, а не сбой:
 * отдаём человеческий текст, а не «500».
 */

import { ApiError } from "./api-client";
import {
  listOrderPipelines,
  listOrderStages,
  transitionOrderToStage,
  ORDER_STAGE_KIND_LABEL,
  type OrderDto,
  type OrderStageConfigDto,
  type OrderStageKind,
} from "./api-orders";

/** Кэш «воронка → её стадии»: карточка живёт минуты, стадии — месяцы. */
const stagesCache = new Map<string, OrderStageConfigDto[]>();

/** Ошибка настройки, а не сети: показываем менеджеру текст, что поправить. */
function configError(kind: OrderStageKind): ApiError {
  return new ApiError(
    {
      code: "order_stage_kind_missing",
      message: `В воронке заказа нет стадии «${ORDER_STAGE_KIND_LABEL[kind]}» — добавьте её в настройках стадий`,
      details: null,
    },
    409,
  );
}

/** Воронка заказа; у старых заказов `pipelineId` пуст — берём воронку по умолчанию. */
async function resolvePipelineId(order: OrderDto): Promise<string> {
  if (order.pipelineId) return order.pipelineId;
  const pipelines = await listOrderPipelines();
  const fallback = pipelines.find((p) => p.isDefault) ?? pipelines[0];
  if (!fallback) {
    throw new ApiError(
      { code: "order_pipeline_missing", message: "У магазина нет воронки заказов", details: null },
      409,
    );
  }
  return fallback.id;
}

/** Стадии воронки с кэшем: повторный клик не ходит в сеть. */
async function pipelineStages(pipelineId: string): Promise<OrderStageConfigDto[]> {
  const cached = stagesCache.get(pipelineId);
  if (cached) return cached;
  const stages = await listOrderStages(pipelineId);
  stagesCache.set(pipelineId, stages);
  return stages;
}

/**
 * Перевести заказ в стадию заданного вида.
 *
 * @throws ApiError — стадии такого вида в воронке нет либо переход запрещён
 *   сервером (сообщение сервера показываем как есть).
 */
export async function transitionOrderToKind(
  order: OrderDto,
  kind: OrderStageKind,
): Promise<OrderDto> {
  const pipelineId = await resolvePipelineId(order);
  const stages = await pipelineStages(pipelineId);
  const target = stages.find((s) => s.kind === kind && s.isActive);
  if (!target) throw configError(kind);
  return transitionOrderToStage(order.id, { toStageId: target.id });
}
